'use client';

import { PortableText as PortableTextReact } from '@portabletext/react';
import type { PortableTextReactComponents } from '@portabletext/react';
import Image from 'next/image';
import Link from 'next/link';
import { urlFor } from '../../../../sanity/lib/image';
import { Info, AlertTriangle, CheckCircle, XCircle, Lightbulb } from 'lucide-react';

// Callout component
function Callout({ type, title, body }: { type: string; title?: string; body?: string }) {
  const config = {
    info: {
      icon: Info,
      bg: 'bg-blue-900/20',
      border: 'border-blue-500/50',
      text: 'text-blue-300',
    },
    warning: {
      icon: AlertTriangle,
      bg: 'bg-amber-900/20',
      border: 'border-amber-500/50',
      text: 'text-amber-300',
    },
    success: {
      icon: CheckCircle,
      bg: 'bg-green-900/20',
      border: 'border-green-500/50',
      text: 'text-green-300',
    },
    error: {
      icon: XCircle,
      bg: 'bg-red-900/20',
      border: 'border-red-500/50',
      text: 'text-red-300',
    },
    tip: {
      icon: Lightbulb,
      bg: 'bg-purple-900/20',
      border: 'border-purple-500/50',
      text: 'text-purple-300',
    },
  };

  const { icon: Icon, bg, border, text } = config[type as keyof typeof config] || config.info;

  return (
    <div className={`my-6 p-4 rounded-lg border ${bg} ${border}`}>
      <div className={`flex items-center gap-2 font-semibold ${text} mb-2`}>
        <Icon className="w-5 h-5" />
        {title || type.charAt(0).toUpperCase() + type.slice(1)}
      </div>
      {body && <p className="text-slate-300 text-sm">{body}</p>}
    </div>
  );
}

// YouTube embed component
function YouTubeEmbed({ url }: { url: string }) {
  const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)?.[1];
  if (!videoId) return null;

  return (
    <div className="my-8 aspect-video rounded-lg overflow-hidden">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title="YouTube video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  );
}

// Code block component
function CodeBlock({ value }: { value: { code: string; language?: string; filename?: string } }) {
  return (
    <div className="my-6">
      {value.filename && (
        <div className="bg-slate-800 text-slate-400 text-xs px-4 py-2 rounded-t-lg border-b border-slate-700">
          {value.filename}
        </div>
      )}
      <pre
        className={`bg-slate-900 border border-slate-800 p-4 overflow-x-auto text-sm ${
          value.filename ? 'rounded-b-lg' : 'rounded-lg'
        }`}
      >
        <code className={`language-${value.language || 'text'} text-slate-300`}>
          {value.code}
        </code>
      </pre>
    </div>
  );
}

const components: Partial<PortableTextReactComponents> = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) return null;
      return (
        <figure className="my-8">
          <div className="relative aspect-video rounded-lg overflow-hidden">
            <Image
              src={urlFor(value).width(1200).url()}
              alt={value.alt || ''}
              fill
              className="object-cover"
            />
          </div>
          {value.caption && (
            <figcaption className="text-center text-sm text-slate-400 mt-3">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
    code: ({ value }) => <CodeBlock value={value} />,
    callout: ({ value }) => (
      <Callout type={value.type}
title={value.title}
body={value.body} />
    ),
    htmlBlock: ({ value }) => (
      <div
        className="my-6 prose prose-invert max-w-none"
        dangerouslySetInnerHTML={{ __html: value.html }}
      />
    ),
    youtube: ({ value }) => <YouTubeEmbed url={value.url} />,
  },
  block: {
    h1: ({ children }) => (
      <h1 className="text-4xl font-bold mt-12 mb-6 text-white">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-3xl font-bold mt-10 mb-4 text-white">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-2xl font-semibold mt-8 mb-3 text-white">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-xl font-semibold mt-6 mb-2 text-white">{children}</h4>
    ),
    normal: ({ children }) => (
      <p className="text-slate-300 leading-relaxed mb-4">{children}</p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-6 pl-6 border-l-4 border-amber-500 italic text-slate-400">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-inside space-y-2 text-slate-300 my-4 ml-4">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-inside space-y-2 text-slate-300 my-4 ml-4">
        {children}
      </ol>
    ),
    check: ({ children }) => (
      <ul className="space-y-2 text-slate-300 my-4 ml-4">{children}</ul>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li>{children}</li>,
    number: ({ children }) => <li>{children}</li>,
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold text-white">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    underline: ({ children }) => <u className="underline">{children}</u>,
    'strike-through': ({ children }) => <s className="line-through">{children}</s>,
    code: ({ children }) => (
      <code className="bg-slate-800 text-amber-400 px-1.5 py-0.5 rounded text-sm font-mono">
        {children}
      </code>
    ),
    highlight: ({ children }) => (
      <mark className="bg-amber-500/30 text-white px-1 rounded">{children}</mark>
    ),
    link: ({ children, value }) => (
      <Link
        href={value?.href || '#'}
        target={value?.blank ? '_blank' : undefined}
        rel={value?.blank ? 'noopener noreferrer' : undefined}
        className="text-amber-500 hover:text-amber-400 underline underline-offset-2 transition-colors"
      >
        {children}
      </Link>
    ),
  },
};

interface PortableTextProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

export default function PortableText({ value }: PortableTextProps) {
  return (
    <PortableTextReact
      value={value}
      components={components}
    />
  );
}

